import { useTransitionNavigate } from '../components/PageTransition';
import { HelmetAsync } from '../components/HelmetAsync';
import { useReveal } from '../hooks/useReveal';
import { SectionLabel } from '../components/ui';
import { Hoverable } from '../components/Hoverable';
import { getByFamily, productPath } from '../data/products';
import { color, font, maxWidth } from '../styles/tokens';

const cardHover = { transform: 'translateY(-6px)', boxShadow: '0 30px 60px -30px rgba(77,141,255,.55)', borderColor: 'rgba(77,141,255,.5)' };

// Stone card: image on top, name/tag, and a "how to order" footer with the order spec.
const StoneCard = ({ product, onClick }) => (
  <Hoverable
    as="button"
    data-reveal=""
    onClick={onClick}
    style={{ textAlign: 'left', background: color.panel, border: `1px solid ${color.panelBorder}`, borderRadius: 18, overflow: 'hidden', cursor: 'pointer', transition: 'transform .35s cubic-bezier(.16,1,.3,1),box-shadow .35s,border-color .35s' }}
    hoverStyle={cardHover}
  >
    <div style={{ position: 'relative', aspectRatio: '16/9', borderBottom: '1px solid rgba(255,255,255,.08)', overflow: 'hidden', background: '#0b0f17' }}>
      <img src={product.img} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
    </div>
    <div style={{ padding: 22 }}>
      <div style={{ font: `600 17px ${font.display}`, color: color.text }}>{product.name}</div>
      <div style={{ marginTop: 8, font: `400 13.5px/1.5 ${font.body}`, color: color.textDim }}>{product.tag}</div>
      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 16, borderTop: '1px solid rgba(255,255,255,.08)' }}>
        <span style={{ font: `500 11px ${font.mono}`, color: color.textFaint, letterSpacing: '.04em' }}>HOW TO ORDER</span>
        <span style={{ font: `700 13px ${font.mono}`, color: color.accent }}>{product.order}</span>
      </div>
    </div>
  </Hoverable>
);

// Tool card: image or hatched placeholder, name/tag, view-details CTA.
const ToolCard = ({ product, onClick }) => (
  <Hoverable
    as="button"
    data-reveal=""
    onClick={onClick}
    style={{ textAlign: 'left', background: color.panel, border: `1px solid ${color.panelBorder}`, borderRadius: 18, padding: 26, cursor: 'pointer', transition: 'transform .35s cubic-bezier(.16,1,.3,1),box-shadow .35s,border-color .35s' }}
    hoverStyle={cardHover}
  >
    {product.hasImg ? (
      <div style={{ position: 'relative', aspectRatio: '16/9', borderRadius: 14, overflow: 'hidden', border: '1px solid rgba(255,255,255,.08)', marginBottom: 18, background: '#0b0f17' }}>
        <img src={product.img} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      </div>
    ) : (
      <div style={{ position: 'relative', aspectRatio: '16/9', borderRadius: 14, background: 'repeating-linear-gradient(135deg,#151b26 0 14px,#10141d 14px 28px)', border: '1px solid rgba(255,255,255,.08)', overflow: 'hidden', marginBottom: 18 }}>
        <span style={{ position: 'absolute', left: 12, bottom: 12, font: `500 9px ${font.mono}`, textTransform: 'uppercase', color: color.textFaint, background: 'rgba(10,12,18,.85)', border: '1px solid rgba(255,255,255,.08)', padding: '4px 7px', borderRadius: 6 }}>
          product photo
        </span>
      </div>
    )}
    <div style={{ font: `600 17px ${font.display}`, color: color.text }}>{product.name}</div>
    <div style={{ marginTop: 9, font: `400 14px/1.6 ${font.body}`, color: color.textDim }}>{product.tag}</div>
    <div style={{ marginTop: 16, font: `500 12px ${font.mono}`, color: color.accent }}>VIEW DETAILS →</div>
  </Hoverable>
);

export const HoningPage = () => {
  const navigate = useTransitionNavigate();
  const stones = getByFamily('Honing Stones');
  const tools = getByFamily('Honing Tools');
  useReveal([]);

  return (
    <div style={{ animation: 'vt-fade .4s ease' }}>
      <HelmetAsync
        title="Honing Solutions — V-Tech Industries"
        description="CBN and diamond honing stones, sticks, mandrels, single-pass tools and fixtures for precise surface finishing and bore sizing."
        keywords="honing stones, honing sticks, CBN, diamond, honing mandrels, single pass honing, honing fixtures"
        url="/honing"
      />

      <section style={{ maxWidth, margin: '0 auto', padding: '80px 28px 24px' }}>
        <SectionLabel data-reveal>[ HONING SOLUTIONS ]</SectionLabel>
        <h1 data-reveal data-delay="80ms" style={{ margin: '18px 0 0', font: `700 clamp(38px,6vw,72px)/0.98 ${font.display}`, letterSpacing: '-.03em', color: color.text }}>
          Honing Stones <span style={{ color: color.accent }}>&amp; Sticks</span>
        </h1>
        <p data-reveal data-delay="140ms" style={{ margin: '24px 0 0', maxWidth: 760, font: `400 17px/1.7 ${font.body}`, color: color.textDim }}>
          A honing stone is an abrasive tool that improves the surface finish and geometry of a workpiece. Using a honing stone (CBN
          or Diamond) to grind the surface allows precise shaping and finishing of metal components for high-quality, robust finishes.
        </p>
      </section>

      <section style={{ maxWidth, margin: '0 auto', padding: '32px 28px 48px' }}>
        <h2 data-reveal style={{ margin: '0 0 26px', font: `600 24px ${font.display}`, color: color.text }}>Stone types</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(290px,1fr))', gap: 20 }}>
          {stones.map((p) => (
            <StoneCard key={p.id} product={p} onClick={() => navigate(productPath(p.id))} />
          ))}
        </div>
      </section>

      <section style={{ background: 'rgba(255,255,255,.015)', borderTop: '1px solid rgba(255,255,255,.07)' }}>
        <div style={{ maxWidth, margin: '0 auto', padding: '64px 28px' }}>
          <h2 data-reveal style={{ margin: '0 0 26px', font: `600 24px ${font.display}`, color: color.text }}>Honing tools &amp; fixtures</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(290px,1fr))', gap: 20 }}>
            {tools.map((p) => (
              <ToolCard key={p.id} product={p} onClick={() => navigate(productPath(p.id))} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
